import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/msalConfig';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { BrandLogo } from '../components/BrandLogo';

function safeReturnTo(value: string | null): string {
  // Only same-app paths; never an absolute URL (open-redirect guard).
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/';
  return value;
}

/** Starts the redirect login automatically so an Oracle deep link lands on the record without a click. */
export function SignInPage() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    setError(null);
    instance.loginRedirect(loginRequest).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    if (isAuthenticated || inProgress !== InteractionStatus.None || started.current) return;
    started.current = true;
    setError(null);
    instance.loginRedirect(loginRequest).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [isAuthenticated, inProgress, instance]);

  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  return (
    <div className="mx-auto mt-20 max-w-md px-4">
      <div className="gsl-panel p-6 text-center">
        <BrandLogo variant="stacked" className="mx-auto w-56" />
        <p className="gsl-display mt-3 text-xl text-muted-foreground">ERP Attach</p>
        <div className="mt-6">
          {inProgress !== InteractionStatus.None ? (
            <Spinner label="Redirecting to Microsoft sign-in" />
          ) : (
            <Button onClick={start}>Sign in with your GSL account</Button>
          )}
        </div>
        {error ? (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
